import { ClientTagBadges } from "../../ClientTags/ClientTags";
import { canManageClientTags } from "intake_shared/src/client_tags";
import * as React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
    ClientStatus,
    DateFormats,
    IClient,
    IClinician,
    SortConfigProps,
} from "intake_shared/src/types";
import { TableHeader } from "../../../../../types";
import moment from "../../../../../utils/app_moment";
import {
    displayPhoneNumber,
    findLabelByPracticeId,
    toTitleCase,
} from "../../../../../utils/utils";
import Table, { TableSortBy } from "../../../bits/Table/Table";
import { Badge } from "../../../bits/Badge/Badge";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import { Link, useLocation } from "react-router-dom";
import {
    getClientRowStylesForInquiryMethod,
    getColorStylesForClientStatus,
    getColorStylesForInquiryMethod,
    getV2BadgePropsByPractice,
} from "../../../../../constants";
import { AclContext } from "../../../../../contexts/AclContext";
import styles from "./ClientList.module.scss";
import ShortenedText from "../../../bits/ShortenedText/ShortenedText";
import { CacheContext } from "../../../../../contexts/CacheContext";
import AssignClient from "../../client-forms/edit-client/EditClientOptions/AssignClient/AssignClient";
import { Button } from "../../../bits/Button/Button";
import EditIcon from "../../../icons/Edit";
import { getGoodFormOnFileStatus } from "../../../../../utils/good_form_on_file";
import {
    getConsentRelevantSessionAttendees,
    isCollateralConsentService,
} from "../../../../../utils/session_attendee_consent";
import { getAmdStatus, isAmdRequired } from "../../../../../utils/amd";
import {
    compareClientsByHeldAppointmentStartDate,
    getHeldAppointmentStartDates,
} from "./appointment_start_dates";
import { getSupervisorIntakeBookingStatus } from "./supervisor_intake_booking_status";
import { getAppointmentHeldDuration } from "../../../../appointment_held";
import { cn } from "../../../../helpers";

interface ClientListTableHeader extends TableHeader {
    sortType?: (rowA: { original: IClient }, rowB: { original: IClient }) => number;
}

const yesLabel = (
    <span
        style={{ fontWeight: "var(--font-bold)", color: "green", fontSize: "16px" }}
    >
        YES
    </span>
);
const noLabel = (
    <span style={{ fontWeight: "var(--font-bold)", color: "red", fontSize: "16px" }}>
        NO
    </span>
);

function ClientList({
    clients,
    status,
    cliniciansLookup,
    allowSelect,
    onSelectActions,
    showActionsInHeader,
    hideStatusColumn,
    enableOtherStatuses,
    pageNumber = 0,
    setSortConfig,
    sortConfig,
    assigneeCounts,
    tableSort,
    onTableSortChange,
}: {
    clients: IClient[];
    status: ClientStatus | "ALL";
    enableOtherStatuses: boolean;
    cliniciansLookup?: { [key: string]: IClinician };
    allowSelect?: boolean;
    pageNumber?: number;
    onSelectActions?: (rows: IClient[]) => JSX.Element[];
    showActionsInHeader?: boolean;
    // Hide the status column when every row is in the same queue
    hideStatusColumn?: boolean;
    setSortConfig?: (value: React.SetStateAction<SortConfigProps>) => void;
    sortConfig?: { key: string; direction: string };
    assigneeCounts?: Record<string, number>;
    tableSort?: TableSortBy;
    onTableSortChange?: (sortBy?: TableSortBy) => void;
}) {
    const browserLocation = useLocation();
    const aclContext = React.useContext(AclContext);
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices = React.useMemo(
        () => olc?.allDepartmentAndServices || {},
        [olc?.allDepartmentAndServices]
    );

    const getPracticesForClient = React.useCallback(
        (clientId) => {
            const client = clients.find((c) => c.id === clientId);

            const heldPractices =
                client?.held_bookings?.map((appointment) => {
                    const practiceId =
                        appointment.practice_id ||
                        (process.env.REACT_APP_MANTLE_PRACTICE_ID as string);

                    const practiceLabel = findLabelByPracticeId(
                        practiceId,
                        allDepartmentsAndServices
                    );

                    return practiceLabel || "HT";
                }) || [];

            // Combine both arrays and remove duplicates
            const allPractices = [...heldPractices];
            return Array.from(new Set(allPractices)).join(", ");
        },
        [clients, allDepartmentsAndServices]
    );

    const [assigneeClient, setAssigneeClient] = React.useState<IClient | null>(null);
    const [showAssignDialog, setShowAssignDialog] = React.useState(false);
    const [clientAssigneeOverrides, setClientAssigneeOverrides] = React.useState<{
        [id: string]: { assigned_to?: string; secondary_assignee?: string };
    }>({});

    const columns = React.useMemo(() => {
        let headers: ClientListTableHeader[] = [
            {
                id: "first_name",
                Header: "First name",
                accessor: (row: IClient) => (
                    <span>{row?.first_name ? row.first_name : "--"}</span>
                ),
            },
            {
                id: "last_name",
                Header: "Last name",
                accessor: (row: IClient) => (
                    <span>{row?.last_name ? row.last_name : "--"}</span>
                ),
            },
        ];

        if (status !== ClientStatus.APPT_HELD) {
            headers.push({
                id: "insurance_provider",
                Header: "Insurance",
                accessor: (row: IClient) => (
                    <span>
                        {row?.insurance_provider ? row.insurance_provider : "--"}
                    </span>
                ),
                canSort: true,
            });
        }

        switch (status) {
            case ClientStatus.INQUIRY:
                headers.push({
                    Header: "Added",
                    id: "on_queue_since",
                    accessor: (row: IClient) => {
                        const convertedDate = (row?.status_history[status] as any)
                            ?.toDate
                            ? (row?.status_history[status] as any).toDate()
                            : (row?.status_history[status] as any);
                        return (
                            <div>{`${moment(convertedDate)?.format(
                                DateFormats.DAY_TIME_FORMAT
                            )}`}</div>
                        );
                    },
                });
                break;
            case ClientStatus.INCOMPLETE_INQUIRY:
                headers.push(
                    {
                        Header: "Added",
                        id: "on_queue_since",
                        accessor: (row: IClient) => {
                            const convertedDate = (
                                row?.status_history[status] as any
                            )?.toDate
                                ? (row?.status_history[status] as any).toDate()
                                : (row?.status_history[status] as any);
                            return (
                                <div>
                                    {convertedDate
                                        ? `${moment(convertedDate)?.format(
                                              DateFormats.DAY_TIME_FORMAT
                                          )}`
                                        : "N/A"}
                                </div>
                            );
                        },
                    },
                    {
                        Header: "Follow-ups",
                        id: "ins_follow_ups",
                        accessor: (row: IClient) => (
                            <>
                                {row[ClientStatus.INCOMPLETE_INQUIRY]
                                    ?.notes_for_incomplete_inquiry ? (
                                    <ShortenedText
                                        notes={
                                            row[ClientStatus.INCOMPLETE_INQUIRY]
                                                ?.notes_for_incomplete_inquiry
                                        }
                                        identifyingKey={`followups-${
                                            row.id || row.email
                                        }`}
                                    />
                                ) : (
                                    "--"
                                )}
                            </>
                        ),
                    }
                );
                break;
            case ClientStatus.FOLLOWUP:
                headers.push(
                    {
                        Header: "Followup Start",
                        id: "follow-start",
                        accessor: (row: IClient) => (
                            <p>
                                {row[ClientStatus.FOLLOWUP]?.start_date
                                    ? moment(
                                          row[ClientStatus.FOLLOWUP]?.start_date
                                      ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                    : "--"}
                            </p>
                        ),
                    },

                    {
                        Header: "Followup End",
                        id: "follow-end",
                        accessor: (row: IClient) => (
                            <p>
                                {row[ClientStatus.FOLLOWUP]?.end_date
                                    ? moment(
                                          row[ClientStatus.FOLLOWUP]?.end_date
                                      ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                    : "--"}
                            </p>
                        ),
                    },
                    {
                        Header: "Followup note",
                        id: "followup-note",
                        accessor: (row: IClient) => (
                            <p>
                                {row[ClientStatus.FOLLOWUP]?.followup_notes ? (
                                    <ShortenedText
                                        notes={
                                            row[ClientStatus.FOLLOWUP]
                                                ?.followup_notes
                                        }
                                        identifyingKey={`followup-note-${
                                            row.id || row.email
                                        }`}
                                    />
                                ) : (
                                    "--"
                                )}
                            </p>
                        ),
                    }
                );
                break;
            case ClientStatus.WAITLIST:
                headers = headers.filter(
                    (h) =>
                        !["phone_no", "pronouns", "insurance_provider"].includes(
                            h.accessor as string
                        )
                );
                headers.push(
                    {
                        Header: "Wants Intern",
                        id: "wants_intern",
                        accessor: (row: IClient) => (
                            <p>{row.waitlist_wants_intern ? yesLabel : noLabel}</p>
                        ),
                    },
                    {
                        Header: "Locations",
                        id: "preferred_locations",
                        accessor: (row: IClient) => (
                            <p>{row.waitlist_locations?.join(", ") || "--"}</p>
                        ),
                    },
                    {
                        Header: "Days",
                        id: "preferred_days",
                        accessor: (row: IClient) => (
                            <p>{row.waitlist_days?.join(", ") || "--"}</p>
                        ),
                    },
                    {
                        Header: "Times",
                        id: "preferred_times",
                        accessor: (row: IClient) => (
                            <p>{row.waitlist_time_categories?.join(", ") || "--"}</p>
                        ),
                    },
                    {
                        Header: "Clinicians",
                        id: "preferred_clinician",
                        accessor: (row: IClient) => (
                            <p>
                                {cliniciansLookup
                                    ? row.waitlist_clinician_ids
                                          ?.map(
                                              (id) => cliniciansLookup[id]?.fullname
                                          )
                                          .join(", ")
                                    : "--"}
                            </p>
                        ),
                    },
                    {
                        Header: "Comments",
                        id: "waitlist_notes",
                        accessor: (row: IClient) => (
                            <ShortenedText
                                notes={row.waitlist_notes || "--"}
                                identifyingKey={row.id || row.email}
                            />
                        ),
                    }
                );
                break;
            case ClientStatus.APPT_HELD:
                headers.push(
                    {
                        Header: "Appt Start Date",
                        id: "appt_start_date",
                        accessor: (row: IClient) => {
                            const appointmentStartDates =
                                getHeldAppointmentStartDates(row);

                            return appointmentStartDates.length ? (
                                <div>
                                    {appointmentStartDates.map(({ id, date }) => (
                                        <div key={id}>
                                            {moment(date).format(
                                                DateFormats.DAY_MONTH_YEAR_FORMAT
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span>--</span>
                            );
                        },
                        sortType: (
                            rowA: { original: IClient },
                            rowB: { original: IClient }
                        ) =>
                            compareClientsByHeldAppointmentStartDate(
                                rowA.original,
                                rowB.original
                            ),
                    },
                    {
                        Header: "Supervisor Intake Booked?",
                        id: "supervisor_intake_booked",
                        accessor: (row: IClient) => {
                            const supervisorIntakeBookingStatus =
                                getSupervisorIntakeBookingStatus(
                                    row,
                                    cliniciansLookup ?? {}
                                );

                            if (supervisorIntakeBookingStatus === "unknown") {
                                return <span>--</span>;
                            }

                            if (supervisorIntakeBookingStatus === "not-required") {
                                return <span>N/A</span>;
                            }

                            return supervisorIntakeBookingStatus === "booked"
                                ? yesLabel
                                : noLabel;
                        },
                    },
                    {
                        Header: "Consent Form Sent",
                        id: "consent_form_sent",
                        accessor: (row: IClient) => {
                            const heldApptsIds = row.held_bookings.map((h) => h.id);
                            const completedConsentForms = heldApptsIds.map(
                                (id) =>
                                    (row.appointment_checks || {})[id]
                                        ?.consent_form_sent
                            );
                            const consentFormDates = completedConsentForms.filter(
                                (date) => date
                            ) as Date[];
                            consentFormDates.sort(
                                (a, b) => a.getTime() - b.getTime()
                            );

                            return heldApptsIds.length > 0 &&
                                consentFormDates.length === heldApptsIds.length ? (
                                <span>
                                    {yesLabel} (
                                    {moment(consentFormDates[0]).format(
                                        DateFormats.DAY_MONTH_FORMAT
                                    )}
                                    )
                                </span>
                            ) : (
                                noLabel
                            );
                        },
                    },
                    {
                        Header: "Good Form on File?",
                        id: "consent_form_rechecked",
                        accessor: (row: IClient) => {
                            const status = getGoodFormOnFileStatus(
                                row.held_bookings.map((b) => b.id),
                                row.appointment_checks
                            );
                            const collateralComplete = row.held_bookings
                                .filter((b) => isCollateralConsentService(b))
                                .every((b) => {
                                    const activeAttendees =
                                        getConsentRelevantSessionAttendees(
                                            b.session_attendees || [],
                                            b.requested_service
                                        );
                                    if (activeAttendees.length === 0) return true;
                                    return activeAttendees.every(
                                        (a) =>
                                            a.consent_check?.good_form_on_file ===
                                            true
                                    );
                                });

                            return status.isComplete && collateralComplete ? (
                                <span>
                                    {yesLabel} (
                                    {moment(status.earliestRecheckedDate).format(
                                        DateFormats.DAY_MONTH_FORMAT
                                    )}
                                    )
                                </span>
                            ) : (
                                noLabel
                            );
                        },
                    },
                    {
                        Header: "Client Added to AMD",
                        id: "client_added_to_amd",
                        accessor: (row: IClient) => {
                            const requiredAppointmentIds = row.held_bookings
                                .filter((booking) => {
                                    const practiceId =
                                        booking.practice_id ||
                                        (process.env
                                            .REACT_APP_MANTLE_PRACTICE_ID as string);
                                    const practiceLabel = findLabelByPracticeId(
                                        practiceId,
                                        allDepartmentsAndServices
                                    );

                                    return isAmdRequired(
                                        practiceLabel,
                                        booking.requested_service
                                    );
                                })
                                .map((booking) => booking.id);
                            const amdStatus = getAmdStatus(
                                requiredAppointmentIds,
                                row.appointment_checks
                            );

                            if (amdStatus.status === "not-applicable") {
                                return <span>N/A</span>;
                            }

                            return amdStatus.status === "complete" ? (
                                <span>
                                    {yesLabel} (
                                    {moment(amdStatus.earliestAddedDate).format(
                                        DateFormats.DAY_MONTH_FORMAT
                                    )}
                                    )
                                </span>
                            ) : (
                                noLabel
                            );
                        },
                    },
                    {
                        Header: "Department",
                        id: "department",
                        accessor: (row: IClient) => {
                            const practices = getPracticesForClient(row.id);
                            return practices ? (
                                <Badge
                                    textColor={
                                        getV2BadgePropsByPractice(practices)
                                            .textColor
                                    }
                                    content={practices}
                                    bgColor={
                                        getV2BadgePropsByPractice(practices).bgColor
                                    }
                                />
                            ) : (
                                <span>--</span>
                            );
                        },
                    }
                );
                break;
            case ClientStatus.APPT_SETTING:
                headers.push(
                    {
                        Header: "Followup Start",
                        id: "follow-start",
                        accessor: (row: IClient) => (
                            <p>
                                {row[ClientStatus.FOLLOWUP]?.start_date
                                    ? moment(
                                          row[ClientStatus.FOLLOWUP]?.start_date
                                      ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                    : "--"}
                            </p>
                        ),
                    },

                    {
                        Header: "Followup End",
                        id: "follow-end",
                        accessor: (row: IClient) => (
                            <p>
                                {row[ClientStatus.FOLLOWUP]?.ending_date
                                    ? moment(
                                          row[ClientStatus.FOLLOWUP]?.ending_date
                                      ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                    : ""}
                            </p>
                        ),
                    },
                    {
                        Header: "Follow-up Notes",
                        id: "follow_up",
                        accessor: (row: IClient) => (
                            <>
                                {row[ClientStatus.FOLLOWUP]?.followup_notes ? (
                                    <ShortenedText
                                        notes={
                                            row[ClientStatus.FOLLOWUP]
                                                ?.followup_notes
                                        }
                                        identifyingKey={`followup-${
                                            row.id || row.email
                                        }`}
                                    />
                                ) : (
                                    "--"
                                )}
                            </>
                        ),
                    },
                    {
                        Header: "Other Follow-up Notes",
                        id: "follow_ups",
                        accessor: (row: IClient) => (
                            <>
                                {row[ClientStatus.APPT_SETTING]?.follow_ups ? (
                                    <ShortenedText
                                        notes={
                                            row[ClientStatus.APPT_SETTING]
                                                ?.follow_ups
                                        }
                                        identifyingKey={`followups-${
                                            row.id || row.email
                                        }`}
                                    />
                                ) : (
                                    "--"
                                )}
                            </>
                        ),
                    }
                );
                break;
            case ClientStatus.FEE_SETTING:
                headers.push({
                    Header: "Follow-ups",
                    id: "fees_follow_ups",
                    accessor: (row: IClient) => (
                        <>
                            {row[ClientStatus.APPT_SETTING]?.follow_ups ? (
                                <ShortenedText
                                    notes={
                                        row[ClientStatus.APPT_SETTING]?.follow_ups
                                    }
                                    identifyingKey={`followups-${
                                        row.id || row.email
                                    }`}
                                />
                            ) : (
                                "--"
                            )}
                        </>
                    ),
                });
                break;
            case ClientStatus.INS_FOLLOWUP:
                headers.push({
                    Header: "Follow-ups",
                    id: "ins_follow_ups",
                    accessor: (row: IClient) => (
                        <>
                            {row[ClientStatus.INS_FOLLOWUP]
                                ?.notes_from_intake_team ? (
                                <ShortenedText
                                    notes={
                                        row[ClientStatus.INS_FOLLOWUP]
                                            ?.notes_from_intake_team
                                    }
                                    identifyingKey={`followups-${
                                        row.id || row.email
                                    }`}
                                />
                            ) : (
                                "--"
                            )}
                        </>
                    ),
                });
                break;
        }

        if (
            !hideStatusColumn &&
            !([ClientStatus.WAITLIST] as any).includes(status)
        ) {
            headers.push({
                Header: "Status",
                id: "status",
                accessor: (row: IClient) => (
                    <Badge
                        content={row.status}
                        textColor="#ffffff"
                        bgColor={getColorStylesForClientStatus(row.status).bgColor}
                    />
                ),
            });
        }

        if (!([ClientStatus.WAITLIST] as any).includes(status)) {
            headers.push({
                Header: "Assignees",
                id: "assignees",
                accessor: (row: IClient) => {
                    const overrides = clientAssigneeOverrides[row.id] || {};
                    const primary = overrides.assigned_to ?? row.assigned_to;
                    const secondary =
                        overrides.secondary_assignee ?? row.secondary_assignee;

                    if (!primary && !secondary) {
                        return (
                            <div className={styles.assigneesContainer}>
                                <span>--</span>
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        <Tooltip id={`edit-assignees-${row.id}`}>
                                            Edit assignees
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="normal"
                                        onClick={() => {
                                            setAssigneeClient(row);
                                            setShowAssignDialog(true);
                                        }}
                                        className={styles.editButton}
                                    >
                                        <EditIcon stroke="var(--color-gray)" />
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        );
                    }

                    return (
                        <div className={styles.assigneesContainer}>
                            <div className={styles.assignees}>
                                {/* Hidden text for copying */}
                                <span className={styles.srOnly}>
                                    {primary && `Primary: ${primary}`}
                                    {primary && secondary && ", "}
                                    {secondary && `Secondary: ${secondary}`}
                                    {!primary && !secondary && "No assignees"}
                                </span>

                                {primary && (
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id={`primary-${row.id}`}>
                                                Primary: {primary}
                                            </Tooltip>
                                        }
                                    >
                                        <div
                                            className={styles.assigneeAvatar}
                                            style={{
                                                borderColor:
                                                    aclContext?.intakeConfig
                                                        ?.intakeOfficerColors?.[
                                                        primary
                                                    ] || "var(--color-primary-v2)",
                                            }}
                                        >
                                            {primary.charAt(0).toUpperCase()}
                                        </div>
                                    </OverlayTrigger>
                                )}
                                {secondary && (
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id={`secondary-${row.id}`}>
                                                Secondary: {secondary}
                                            </Tooltip>
                                        }
                                    >
                                        <div
                                            className={styles.assigneeAvatar}
                                            style={{
                                                borderColor:
                                                    aclContext?.intakeConfig
                                                        ?.intakeOfficerColors?.[
                                                        secondary
                                                    ] || "var(--color-primary-v2)",
                                            }}
                                        >
                                            {secondary.charAt(0).toUpperCase()}
                                        </div>
                                    </OverlayTrigger>
                                )}
                            </div>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id={`edit-assignees-${row.id}`}>
                                        Edit assignees
                                    </Tooltip>
                                }
                            >
                                <Button
                                    onClick={() => {
                                        setAssigneeClient(row);
                                        setShowAssignDialog(true);
                                    }}
                                    variant="normal"
                                    className={styles.editButton}
                                >
                                    <EditIcon stroke="var(--color-gray)" />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    );
                },
            });
        }

        if (ClientStatus.INS_VERIFICATION === status) {
            headers.splice(2, 0, {
                Header: "DOB",
                id: "date_of_birth",
                accessor: (row: IClient) => (
                    <span>
                        {row?.date_of_birth
                            ? moment(row?.date_of_birth).format(
                                  DateFormats.DAY_MONTH_YEAR_FORMAT
                              )
                            : "--"}
                    </span>
                ),
            });

            headers.splice(4, 0, {
                Header: "Insurance Member ID",
                id: "insurance_member_id",
                accessor: (row: IClient) => <span>{row.insurance_member_id}</span>,
            });
            headers.splice(0, 0, {
                Header: "Inquiry method",
                id: "inquiry_method",
                accessor: (row: IClient) => (
                    <span>
                        {row.inquiry_method ? (
                            <Badge
                                content={toTitleCase(row.inquiry_method)}
                                textColor="#ffffff"
                                bgColor={
                                    getColorStylesForInquiryMethod(
                                        row.inquiry_method
                                    ).bgColor
                                }
                            />
                        ) : (
                            "--"
                        )}
                    </span>
                ),
            });
        } else {
            headers.splice(
                0,
                0,
                ...[
                    {
                        Header: "Inquiry method",
                        id: "inquiry_method",
                        accessor: (row: IClient) => (
                            <span>
                                {row.inquiry_method ? (
                                    <Badge
                                        content={toTitleCase(row.inquiry_method)}
                                        textColor="#ffffff"
                                        bgColor={
                                            getColorStylesForInquiryMethod(
                                                row.inquiry_method
                                            ).bgColor
                                        }
                                    />
                                ) : (
                                    "--"
                                )}
                            </span>
                        ),
                    },
                    {
                        Header: "Email",
                        id: "email",
                        accessor: (row: IClient) => <span>{row.email || "--"}</span>,
                    },
                    {
                        id: "phone_no",
                        Header: "Phone no",
                        accessor: (row: IClient) => (
                            <span className={styles.phoneNo}>
                                {row.phone_no
                                    ? displayPhoneNumber(row.phone_no)
                                    : "--"}
                            </span>
                        ),
                    },
                ]
            );
            if (status !== ClientStatus.APPT_HELD) {
                headers.splice(2, 0, {
                    id: "pronouns",
                    Header: "Pronouns",
                    accessor: (row: IClient) => <span>{row.pronouns || "--"}</span>,
                });
            }
        }

        if (status === ClientStatus.APPT_HELD) {
            headers.splice(0, 0, {
                Header: "Appt Held",
                id: "appointment_held_duration",
                accessor: (row: IClient) => {
                    const duration = getAppointmentHeldDuration(
                        row.status_history[ClientStatus.APPT_HELD]
                    );

                    if (!duration) {
                        return <span>--</span>;
                    }

                    return (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip id={`appointment-held-${row.id}`}>
                                    Entered Appointment Held:{" "}
                                    {moment(duration.enteredAt).format(
                                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                                    )}
                                </Tooltip>
                            }
                        >
                            <span
                                className={cn(styles.appointmentHeldDuration, {
                                    [styles.appointmentHeldOverdue]:
                                        duration.isOverdue,
                                })}
                            >
                                {duration.label}
                            </span>
                        </OverlayTrigger>
                    );
                },
            });
        }

        headers.push(
            ...[
                {
                    Header: "",
                    id: "view-client-from-list",
                    accessor: (row: IClient) => (
                        <Link
                            to={{
                                pathname: `/v2/client-full-view`,
                                search: `?clientId=${row.id}&activeTab=${
                                    status === ClientStatus.WAITLIST
                                        ? ClientStatus.INQUIRY
                                        : status
                                }&beforeId=${clients[
                                    clients.length - 1
                                ]?.date_updated?.toISOString()}&page=${pageNumber}`,
                                state: {
                                    client: row,
                                    enableOtherStatuses,
                                    returnPathname: browserLocation.pathname,
                                    returnSearch: browserLocation.search,
                                    returnState: browserLocation.state,
                                },
                            }}
                        >
                            View
                        </Link>
                    ),
                },
            ]
        );

        if (canManageClientTags(aclContext?.authUser?.acl)) {
            headers.splice(headers.length - 1, 0, {
                Header: "Tags",
                id: "client_tags",
                accessor: (row: IClient) => (
                    <ClientTagBadges tags={row.tags} compact />
                ),
            });
        }
        return headers;
    }, [
        status,
        cliniciansLookup,
        getPracticesForClient,
        allDepartmentsAndServices,
        aclContext?.intakeConfig?.intakeOfficerColors,
        aclContext?.authUser?.acl,
        clients,
        pageNumber,
        enableOtherStatuses,
        clientAssigneeOverrides,
        browserLocation.pathname,
        browserLocation.search,
        browserLocation.state,
        hideStatusColumn,
    ]);

    const sortableColumns = [
        "first_name",
        "last_name",
        "insurance_provider",
        "email",
        "phone_no",
        "pronouns",
        "inquiry_method",
        "insurance_member_id",
        "date_of_birth",
    ];

    return (
        <>
            {clients.length === 0 ? (
                <EmptyListState emptyMessage="No clients found" />
            ) : (
                <Table
                    columns={columns}
                    data={clients}
                    allowSelect={Boolean(allowSelect)}
                    onSelectActions={onSelectActions}
                    showActionsInHeader={showActionsInHeader}
                    styles={{ wordbreak: true }}
                    stickyLastColumn
                    setSortConfig={setSortConfig}
                    sortConfig={sortConfig}
                    sortableColumns={sortableColumns}
                    assigneeCounts={assigneeCounts}
                    initialSortBy={tableSort}
                    controlledSortColumn="appt_start_date"
                    onSortByChange={onTableSortChange}
                    handleRowStyle={(row) =>
                        getClientRowStylesForInquiryMethod(
                            row.original.inquiry_method
                        )
                    }
                />
            )}
            {showAssignDialog && assigneeClient && (
                <AssignClient
                    client={assigneeClient}
                    onSaveClient={() => {
                        /* no-op in list context */
                    }}
                    setActiveKey={() => {
                        /* no-op */
                    }}
                    open={showAssignDialog}
                    onClose={() => setShowAssignDialog(false)}
                    hideTriggerButton={true}
                    onAssigned={(updated) => {
                        setShowAssignDialog(false);
                        setClientAssigneeOverrides((prev) => ({
                            ...prev,
                            [updated.id]: {
                                assigned_to: updated.assigned_to,
                                secondary_assignee: updated.secondary_assignee,
                            },
                        }));
                    }}
                />
            )}
        </>
    );
}

export default ClientList;
